import { useEffect, useRef, useState } from 'react'
import {
  ArrowDown, ArrowUp, CheckCircle2, CircleHelp, Download, Gauge, Info, Languages, Plus, RefreshCw, RotateCcw, Server,
  ShieldCheck, Smartphone, Sparkles, TestTube2, Trash2, Tv, Upload, UserRound, XCircle,
} from 'lucide-react'
import { APP_VERSION, DEFAULT_SETTINGS } from '../constants'
import { detectCodecCapabilities, type CodecSupport } from '../lib/codecs'
import { formatBytesPerSecond } from '../lib/format'
import { clearAppCache, getMeasuredSpeed } from '../lib/storage'
import { getAddonManifest, isHttpUrl, testAddon, testServer } from '../lib/stremio'
import { addonId, withAddons } from '../lib/addons'
import { applySyncSnapshot, downloadSyncSnapshot, SYNC_FILE_MAX_BYTES } from '../lib/sync'
import { createHouseholdProfile, deleteHouseholdProfile, getHouseholdProfiles, switchHouseholdProfile } from '../lib/profiles'
import { knownLanguages, languageName } from '../lib/subtitles'
import type { HdrCapabilities } from '../lib/hdr'
import type { TvRemoteConnectionState } from '../hooks/useTvRemote'
import type { AddonResource, HdrMode, Settings, SubtitleSize } from '../types'
import { PairingPanel } from './PairingPanel'

interface Props {
  settings: Settings
  onSave: (value: Settings) => void
  remoteConnection: TvRemoteConnectionState
  hdrCapabilities: HdrCapabilities
  onOpenAdmin: () => void
  features: { phoneRemote: boolean; hdrDiagnostics: boolean }
}

type TestState = { kind: 'idle' | 'loading' | 'ok' | 'error'; message?: string }
type CodecResult = { label: string; support: 'probably' | 'maybe' | 'no' }

const LANGUAGES = ['eng', 'ces', 'deu', 'spa', 'fra', 'ita', 'pol']

/**
 * The shortlist covers most of this TV's viewers, but the storage layer accepts
 * any code and the app can name 38 — so a Dutch or Portuguese household should
 * not be stuck. The current choice is always shown, even when it is not on the
 * shortlist, so it never looks unset.
 */
function LanguageChoice({ value, navKey, onChange }: { value: string; navKey: string; onChange: (code: string) => void }) {
  const [all, setAll] = useState(false)
  // `value` is always listed: a code carried in by a sync file may not appear
  // in either list, and the current choice must never look unset.
  const codes = Array.from(new Set([...(all ? knownLanguages() : LANGUAGES), value]))
  return (
    <div className="choice-row" data-nav-group="row" data-nav-key={navKey}>
      {codes.map((code) => (
        <button className={`choice-button${value === code ? ' selected' : ''}`} data-focusable onClick={() => onChange(code)} key={code}>{languageName(code)}</button>
      ))}
      <button className="choice-button" data-focusable onClick={() => setAll((shown) => !shown)}>{all ? 'Fewer' : 'More languages'}</button>
    </div>
  )
}

function probeCodecs(): CodecResult[] {
  const capabilities = detectCodecCapabilities()
  const checks: Array<[string, CodecSupport]> = [
    ['H.264', capabilities.h264], ['HEVC / H.265', capabilities.hevc],
    ['VP9', capabilities.vp9], ['AV1', capabilities.av1],
    ['AAC', capabilities.aac], ['Dolby Digital', capabilities.ac3],
    ['Dolby Digital Plus', capabilities.eac3], ['Opus', capabilities.opus],
    ['Native HLS', capabilities.hls],
  ]
  return checks.map(([label, support]) => ({
    label,
    support: support === 'supported' ? 'probably' : support === 'maybe' ? 'maybe' : 'no',
  }))
}

export function SettingsScreen({ settings, onSave, remoteConnection, hdrCapabilities, onOpenAdmin, features }: Props) {
  const [serverUrl, setServerUrl] = useState(settings.serverUrl)
  const [torrentioUrl, setTorrentioUrl] = useState(settings.torrentioUrl)
  const [subtitlesUrl, setSubtitlesUrl] = useState(settings.subtitlesUrl)
  const [serverTest, setServerTest] = useState<TestState>({ kind: 'idle' })
  const [addonTest, setAddonTest] = useState<TestState>({ kind: 'idle' })
  const [cacheState, setCacheState] = useState<TestState>({ kind: 'idle' })
  const [codecResults, setCodecResults] = useState<CodecResult[]>([])
  const [subtitleAddressError, setSubtitleAddressError] = useState('')
  const [pairing, setPairing] = useState(false)
  const [newAddonUrl, setNewAddonUrl] = useState('')
  const [newAddonState, setNewAddonState] = useState<TestState>({ kind: 'idle' })
  const [profiles, setProfiles] = useState(getHouseholdProfiles)
  const [profileName, setProfileName] = useState('')
  const [syncState, setSyncState] = useState<TestState>({ kind: 'idle' })
  const [confirmReset, setConfirmReset] = useState(false)
  const syncInput = useRef<HTMLInputElement>(null)
  const measured = getMeasuredSpeed()

  useEffect(() => {
    setServerUrl(settings.serverUrl)
    setTorrentioUrl(settings.torrentioUrl)
    setSubtitlesUrl(settings.subtitlesUrl)
  }, [settings.serverUrl, settings.subtitlesUrl, settings.torrentioUrl])

  async function checkServer() {
    if (!isHttpUrl(serverUrl)) { setServerTest({ kind: 'error', message: 'Enter a valid HTTP or HTTPS address.' }); return }
    setServerTest({ kind: 'loading' })
    try {
      const version = await testServer(serverUrl)
      onSave({ ...settings, serverUrl })
      setServerTest({ kind: 'ok', message: `Connected, version ${version}` })
    } catch (error) {
      setServerTest({ kind: 'error', message: error instanceof Error ? error.message : 'Connection failed' })
    }
  }

  async function checkAddon() {
    if (!isHttpUrl(torrentioUrl)) { setAddonTest({ kind: 'error', message: 'Enter a valid HTTP or HTTPS address.' }); return }
    setAddonTest({ kind: 'loading' })
    try {
      const name = await testAddon(torrentioUrl)
      onSave({ ...settings, torrentioUrl })
      setAddonTest({ kind: 'ok', message: `${name} is ready` })
    } catch (error) {
      setAddonTest({ kind: 'error', message: error instanceof Error ? error.message : 'Connection failed' })
    }
  }

  async function clearCache() {
    setCacheState({ kind: 'loading' })
    try {
      await clearAppCache()
      setCacheState({ kind: 'ok', message: 'Temporary cache and speed samples cleared' })
    } catch {
      setCacheState({ kind: 'error', message: 'Browser cache could not be cleared' })
    }
  }

  function saveAddons(addons: Settings['addons']) {
    onSave(withAddons(settings, addons))
  }

  async function addAddon() {
    if (!isHttpUrl(newAddonUrl)) { setNewAddonState({ kind: 'error', message: 'Enter a valid manifest URL.' }); return }
    const url = newAddonUrl.trim().replace(/\/manifest\.json\/?$/i, '').replace(/\/+$/, '')
    const id = addonId(url)
    const replacing = settings.addons.some((addon) => addon.id === id || addon.url === url)
    if (!replacing && settings.addons.length >= 12) {
      setNewAddonState({ kind: 'error', message: 'Vela supports up to 12 add-on providers. Remove one first.' })
      return
    }
    setNewAddonState({ kind: 'loading' })
    try {
      const manifest = await getAddonManifest(newAddonUrl)
      const resources = (manifest.resources || []).map((resource) => typeof resource === 'string'
        ? resource
        : resource && typeof resource === 'object' && 'name' in resource && typeof resource.name === 'string' ? resource.name : '')
        .filter((resource): resource is AddonResource => ['catalog', 'meta', 'stream', 'subtitles'].includes(resource))
      const next = settings.addons.filter((addon) => addon.id !== id && addon.url !== url)
      next.push({ id, name: manifest.name || new URL(url).hostname, url, enabled: true, priority: next.length, resources: resources.length ? resources : undefined })
      saveAddons(next)
      setNewAddonUrl('')
      setNewAddonState({ kind: 'ok', message: `${manifest.name || 'Add-on'} added` })
    } catch (error) {
      setNewAddonState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not read manifest.' })
    }
  }

  async function importSync(file?: File) {
    if (!file) return
    if (file.size > SYNC_FILE_MAX_BYTES) {
      setSyncState({ kind: 'error', message: 'Vela sync files must be 512 KiB or smaller.' })
      if (syncInput.current) syncInput.current.value = ''
      return
    }
    setSyncState({ kind: 'loading' })
    try {
      applySyncSnapshot(await file.text(), settings)
      setSyncState({ kind: 'ok', message: 'Library, progress and preferences merged.' })
      window.setTimeout(() => location.reload(), 600)
    } catch (error) {
      setSyncState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not import sync file.' })
    } finally {
      // Let the same file be selected again after a failed import.
      if (syncInput.current) syncInput.current.value = ''
    }
  }

  return (
    <div className="screen settings-screen">
      <header className="screen-title"><h1>Settings</h1><p>Changes apply immediately. Personal data stays on this TV.</p></header>

      <div className="settings-grid">
        {features.phoneRemote ? (
        <section className="settings-card featured-settings-card">
          <div className="settings-icon"><Smartphone size="2rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row">
              <div><h2>Phone remote</h2><p>Pair once, then browse and control Vela from your phone forever.</p><small className="remote-setting-status"><i className={remoteConnection.status === 'connected' ? 'online' : ''} />{remoteConnection.status === 'connected' ? `${remoteConnection.controllers} controller${remoteConnection.controllers === 1 ? '' : 's'} connected` : remoteConnection.status === 'idle' ? 'Not paired' : 'Reconnecting'}</small></div>
              <button className="primary-button" data-focusable data-autofocus data-screen-entry="settings" data-nav-up-section="settings" onClick={() => setPairing(true)}><Smartphone size="1.5rem" /> Pair / manage</button>
            </div>
          </div>
        </section>
        ) : null}

        <section className="settings-card">
          <div className="settings-icon"><Server size="2rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row"><div><h2>Streaming server</h2><p>Torrents are fetched and remuxed on your server, not on the TV.</p></div><button className="secondary-button" data-focusable onClick={() => { setServerUrl(DEFAULT_SETTINGS.serverUrl); onSave({ ...settings, serverUrl: DEFAULT_SETTINGS.serverUrl }) }}><Sparkles size="1.5rem" /> Vela preset</button></div>
            <label><span>Server address</span><input value={serverUrl} onChange={(event) => setServerUrl(event.target.value)} onBlur={() => {
              if (serverUrl === settings.serverUrl) return
              if (isHttpUrl(serverUrl)) onSave({ ...settings, serverUrl })
              else setServerTest({ kind: 'error', message: 'Address was not applied. Enter HTTP or HTTPS.' })
            }} data-focusable spellCheck={false} /></label>
            <div className="setting-actions" data-nav-group="row" data-nav-key="server-actions">
              <button className="secondary-button" data-focusable onClick={checkServer}>{serverTest.kind === 'loading' ? <RefreshCw className="spin" size="1.5rem" /> : <TestTube2 size="1.5rem" />} Test connection</button>
              <TestResult state={serverTest} />
            </div>
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-icon"><UserRound size="2rem" /></div>
          <div className="settings-copy">
            <h2>Profiles and device sync</h2>
            <p>Keep separate household history, use a paired phone to sync TVs privately, or move a portable file without an account.</p>
            <div className="choice-row" data-nav-group="row" data-nav-key="household-profiles">
              {profiles.map((profile) => <button className={`choice-button${profile.active ? ' selected' : ''}`} data-focusable onClick={() => { if (!profile.active) { switchHouseholdProfile(profile.id, settings); location.reload() } }} key={profile.id}>{profile.name}</button>)}
            </div>
            <div className="setting-actions" data-nav-group="row" data-nav-key="profile-actions">
              <input value={profileName} maxLength={24} placeholder="New profile name" onChange={(event) => setProfileName(event.target.value)} data-focusable />
              <button className="secondary-button" data-focusable onClick={() => { try { setProfiles(createHouseholdProfile(profileName, settings)); setProfileName('') } catch (error) { setSyncState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not add profile.' }) } }}><Plus size="1.5rem" /> Add profile</button>
              <button className="secondary-button" data-focusable disabled={!profiles.some((profile) => !profile.active)} onClick={() => { const inactive = profiles.filter((profile) => !profile.active); const target = inactive[inactive.length - 1]; if (target) setProfiles(deleteHouseholdProfile(target.id)) }}><Trash2 size="1.5rem" /> Remove last inactive</button>
            </div>
            <div className="setting-actions" data-nav-group="row" data-nav-key="sync-actions">
              <button className="secondary-button" data-focusable onClick={() => {
                // An installed webOS app has no download manager, so this can
                // fail silently. Say which way it went either way.
                setSyncState(downloadSyncSnapshot(settings)
                  ? { kind: 'ok', message: 'Sync file saved.' }
                  : { kind: 'error', message: 'This TV cannot save files. Use a paired phone to carry the snapshot instead.' })
              }}><Download size="1.5rem" /> Export sync file</button>
              <button className="secondary-button" data-focusable onClick={() => syncInput.current?.click()}><Upload size="1.5rem" /> Import and merge</button>
              <input ref={syncInput} className="visually-hidden" type="file" accept="application/json,.json" onChange={(event) => importSync(event.target.files?.[0])} />
              <TestResult state={syncState} />
            </div>
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-icon purple"><Tv size="2rem" /></div>
          <div className="settings-copy">
            <h2>HDR playback</h2>
            <p>Auto chooses only a confirmed HDR path. Prefer HDR can try an unconfirmed format; SDR only is the safe fallback.</p>
            <div className="choice-row" data-nav-group="row" data-nav-key="hdr-mode">
              {(['auto', 'prefer', 'sdr'] as HdrMode[]).map((mode) => (
                <button className={`choice-button${settings.hdrMode === mode ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, hdrMode: mode })} key={mode}>
                  {mode === 'auto' ? 'Auto' : mode === 'prefer' ? 'Prefer HDR' : 'SDR only'}
                </button>
              ))}
            </div>
            {features.hdrDiagnostics ? <p className="settings-footnote">Display: {hdrCapabilities.display} · HDR10: {hdrCapabilities.hdr10} · HDR10+: {hdrCapabilities.hdr10Plus} · Dolby Vision: {hdrCapabilities.dolbyVision}. Panel HDR badge remains the final live confirmation.</p> : null}
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-icon purple"><ShieldCheck size="2rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row">
              <div><h2>Stremio add-on</h2><p>Provides playable files and any movie or series catalogs declared in its manifest.</p></div>
              <button className={`toggle${settings.torrentioEnabled ? ' on' : ''}`} data-focusable onClick={() => onSave({ ...settings, torrentioEnabled: !settings.torrentioEnabled })} role="switch" aria-checked={settings.torrentioEnabled} aria-label="Use stream add-ons"><i /></button>
            </div>
            <label><span>Add-on address</span><input value={torrentioUrl} onChange={(event) => setTorrentioUrl(event.target.value)} onBlur={() => {
              if (torrentioUrl === settings.torrentioUrl) return
              if (isHttpUrl(torrentioUrl)) onSave({ ...settings, torrentioUrl })
              else setAddonTest({ kind: 'error', message: 'Address was not applied. Enter HTTP or HTTPS.' })
            }} data-focusable spellCheck={false} /></label>
            <div className="setting-actions" data-nav-group="row" data-nav-key="addon-actions">
              <button className="secondary-button" data-focusable onClick={checkAddon}>{addonTest.kind === 'loading' ? <RefreshCw className="spin" size="1.5rem" /> : <TestTube2 size="1.5rem" />} Test add-on</button>
              <button className="secondary-button" data-focusable onClick={() => { setTorrentioUrl(DEFAULT_SETTINGS.torrentioUrl); onSave({ ...settings, torrentioUrl: DEFAULT_SETTINGS.torrentioUrl, torrentioEnabled: true }) }}><RotateCcw size="1.5rem" /> Torrentio preset</button>
              <TestResult state={addonTest} />
            </div>
            <div className="addon-manager-list" data-nav-group="col" data-nav-key="addon-manager">
              {settings.addons.map((addon, index) => (
                <div className="addon-manager-row" key={addon.id}>
                  <span><strong>{addon.name}</strong><small>{addon.resources?.join(' · ') || 'manifest resources'} · priority {index + 1}</small></span>
                  <button className={`toggle${addon.enabled ? ' on' : ''}`} data-focusable onClick={() => saveAddons(settings.addons.map((entry) => entry.id === addon.id ? { ...entry, enabled: !entry.enabled } : entry))} role="switch" aria-checked={addon.enabled} aria-label={`${addon.enabled ? 'Disable' : 'Enable'} ${addon.name}`}><i /></button>
                  <button data-focusable disabled={index === 0} onClick={() => { const next = [...settings.addons]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; saveAddons(next) }} aria-label={`Move ${addon.name} up`}><ArrowUp size="1.5rem" /></button>
                  <button data-focusable disabled={index === settings.addons.length - 1} onClick={() => { const next = [...settings.addons]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; saveAddons(next) }} aria-label={`Move ${addon.name} down`}><ArrowDown size="1.5rem" /></button>
                  <button data-focusable onClick={() => saveAddons(settings.addons.filter((entry) => entry.id !== addon.id))} aria-label={`Remove ${addon.name}`}><Trash2 size="1.5rem" /></button>
                </div>
              ))}
            </div>
            <label><span>Add manifest URL</span><input value={newAddonUrl} onChange={(event) => { setNewAddonUrl(event.target.value); setNewAddonState({ kind: 'idle' }) }} data-focusable spellCheck={false} /></label>
            <div className="setting-actions"><button className="secondary-button" data-focusable disabled={newAddonState.kind === 'loading'} onClick={addAddon}>{newAddonState.kind === 'loading' ? <RefreshCw className="spin" size="1.5rem" /> : <Plus size="1.5rem" />} Add provider</button><TestResult state={newAddonState} /></div>
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-icon"><Languages size="2rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row"><div><h2>Subtitles</h2><p>OpenSubtitles language and text size are remembered for every title.</p></div><button className={`toggle${settings.subtitlesEnabled ? ' on' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitlesEnabled: !settings.subtitlesEnabled })} role="switch" aria-checked={settings.subtitlesEnabled} aria-label="Enable subtitles"><i /></button></div>
            <div className="setting-option"><span>Preferred language</span><LanguageChoice value={settings.subtitleLanguage} navKey="subtitle-language" onChange={(code) => onSave({ ...settings, subtitleLanguage: code, subtitlesEnabled: true })} /></div>
            <div className="setting-option"><span>Text size</span><div className="choice-row" data-nav-group="row" data-nav-key="subtitle-size-settings">{(['small', 'medium', 'large'] as SubtitleSize[]).map((size) => <button className={`choice-button${settings.subtitleSize === size ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitleSize: size })} key={size}>{size}</button>)}</div></div>
            <div className="setting-option"><span>Position</span><div className="choice-row" data-nav-group="row" data-nav-key="subtitle-position-settings">{(['bottom', 'middle', 'top'] as const).map((value) => <button className={`choice-button${settings.subtitlePosition === value ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitlePosition: value })} key={value}>{value}</button>)}</div></div>
            <div className="setting-option"><span>Background</span><div className="choice-row" data-nav-group="row" data-nav-key="subtitle-background-settings">{(['none', 'shadow', 'box'] as const).map((value) => <button className={`choice-button${settings.subtitleBackground === value ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitleBackground: value })} key={value}>{value}</button>)}</div></div>
            <div className="setting-option"><span>Forced subtitles</span><div className="choice-row" data-nav-group="row" data-nav-key="subtitle-forced-settings">{(['include', 'prefer', 'only'] as const).map((value) => <button className={`choice-button${settings.subtitleForced === value ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitleForced: value })} key={value}>{value}</button>)}</div></div>
            <div className="setting-option"><span>SDH / captions</span><div className="choice-row" data-nav-group="row" data-nav-key="subtitle-sdh-settings">{(['include', 'prefer', 'exclude'] as const).map((value) => <button className={`choice-button${settings.subtitleSdh === value ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitleSdh: value })} key={value}>{value}</button>)}</div></div>
            <div className="setting-option"><span>Encoding repair</span><div className="choice-row" data-nav-group="row" data-nav-key="subtitle-encoding-settings">{(['auto', 'utf-8', 'windows-1252'] as const).map((value) => <button className={`choice-button${settings.subtitleEncoding === value ? ' selected' : ''}`} data-focusable onClick={() => onSave({ ...settings, subtitleEncoding: value })} key={value}>{value === 'windows-1252' ? 'Legacy' : value}</button>)}</div></div>
            <label><span>Subtitle add-on address</span><input value={subtitlesUrl} onChange={(event) => { setSubtitlesUrl(event.target.value); setSubtitleAddressError('') }} onBlur={() => {
              if (subtitlesUrl === settings.subtitlesUrl) return
              if (isHttpUrl(subtitlesUrl)) onSave({ ...settings, subtitlesUrl })
              else setSubtitleAddressError('Address was not applied. Enter HTTP or HTTPS.')
            }} data-focusable spellCheck={false} /></label>
            {subtitleAddressError ? <p className="settings-footnote settings-error">{subtitleAddressError}</p> : null}
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-icon"><Gauge size="2rem" /></div>
          <div className="settings-copy">
            <h2>Playback</h2>
            <SettingToggle label="Prefer HLS" detail="Best format compatibility on LG webOS." value={settings.preferHls} onChange={(value) => onSave({ ...settings, preferHls: value })} />
            <SettingToggle label="Autoplay next episode" detail="Continue a series without returning to its page." value={settings.autoplayNext} onChange={(value) => onSave({ ...settings, autoplayNext: value })} />
            <div className="setting-option"><span>Preferred audio</span><LanguageChoice value={settings.preferredAudioLanguage} navKey="audio-language-settings" onChange={(code) => onSave({ ...settings, preferredAudioLanguage: code })} /></div>
            <SettingToggle label="Restore interrupted playback" detail="Reopen the exact local title and source after webOS relaunches Vela." value={settings.restorePlayback} onChange={(value) => onSave({ ...settings, restorePlayback: value })} />
            <SettingToggle label="OLED pause protection" detail="Dim static paused video and move the pause mark before burn-in can build." value={settings.oledProtection} onChange={(value) => onSave({ ...settings, oledProtection: value })} />
            <p className="settings-footnote">{measured ? `Measured connection speed: ${formatBytesPerSecond(measured)}. Sources are ranked against this.` : 'Vela will measure your connection during playback and use it to rank sources.'}</p>
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-icon"><Tv size="2rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row"><div><h2>This TV</h2><p>Ask the webOS media engine what it says it can decode.</p></div><button className="secondary-button" data-focusable onClick={() => setCodecResults(probeCodecs())}><TestTube2 size="1.5rem" /> Probe codecs</button></div>
            {codecResults.length ? <div className="codec-results">{codecResults.map((result) => <span className={result.support} key={result.label}>{result.support === 'no' ? <XCircle size="1.25rem" /> : <CheckCircle2 size="1.25rem" />}{result.label}<small>{result.support}</small></span>)}</div> : <div className="settings-inline-note"><CircleHelp size="1.5rem" /> Results are capability hints from the browser; real files remain the final test.</div>}
          </div>
        </section>

        <section className="settings-card compact-settings">
          <div className="settings-copy">
            <div className="setting-title-row">
              <div><h3>Temporary cache</h3><p>Clear browser cache and old speed measurements. Library and progress stay.</p></div>
              <div className="setting-actions"><button className="secondary-button" data-focusable onClick={clearCache}><Trash2 size="1.5rem" /> Clear cache</button><TestResult state={cacheState} /></div>
            </div>
          </div>
        </section>

        <section className="settings-card compact-settings">
          <div className="settings-icon"><ShieldCheck size="1.75rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row">
              <div>
                <h3>Admin controls</h3>
                <p>Choose which destinations, rows and features this TV offers, and how it looks.</p>
              </div>
              <button className="secondary-button" data-focusable onClick={onOpenAdmin}>
                <ShieldCheck size="1.5rem" /> Open
              </button>
            </div>
          </div>
        </section>

        <section className="settings-card compact-settings about-card">
          <div className="settings-icon"><Info size="1.75rem" /></div>
          <div className="settings-copy">
            <div className="setting-title-row">
              <div><h3>Vela {APP_VERSION}</h3><p>Remote-first Stremio client · webOS 6.0+ · settings stored locally.</p></div>
              {/* One press used to wipe the server address, every add-on and all
                  subtitle preferences. The admin reset asks first; so does this. */}
              {confirmReset ? (
                <div className="setting-actions" data-nav-group="row" data-nav-key="reset-confirm">
                  <button className="secondary-button danger-button" data-focusable data-autofocus onClick={() => { onSave({ ...DEFAULT_SETTINGS }); setConfirmReset(false) }}><RotateCcw size="1.5rem" /> Yes, reset everything</button>
                  <button className="secondary-button" data-focusable onClick={() => setConfirmReset(false)}>Keep my settings</button>
                </div>
              ) : (
                <button className="secondary-button" data-focusable onClick={() => setConfirmReset(true)}><RotateCcw size="1.5rem" /> Reset defaults</button>
              )}
            </div>
          </div>
        </section>
      </div>
      {pairing && features.phoneRemote ? <PairingPanel values={settings} onPair={(values) => onSave({ ...settings, ...values })} onClose={() => setPairing(false)} /> : null}
    </div>
  )
}

function TestResult({ state }: { state: TestState }) {
  if (state.kind === 'idle' || state.kind === 'loading') return null
  return <span className={`test-result ${state.kind}`}>{state.kind === 'ok' ? <CheckCircle2 size="1.5rem" /> : <XCircle size="1.5rem" />}{state.message}</span>
}

function SettingToggle({ label, detail, value, onChange }: { label: string; detail: string; value: boolean; onChange: (value: boolean) => void }) {
  return (
    <div className="setting-title-row divider">
      <div><h3>{label}</h3><p>{detail}</p></div>
      <button className={`toggle${value ? ' on' : ''}`} data-focusable role="switch" aria-checked={value} onClick={() => onChange(!value)} aria-label={label}><i /></button>
    </div>
  )
}
